import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Modal, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';

import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import { getMappedUsers, confirmDelete } from './ProjectMappedUserService';
import MapUserModal from './MapUserModal';

const MappedUsersListTables = () => {
    document.title = "Mapped Users | Valitan";

    const [mappedUsers, setMappedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [projectModalOpen, setProjectModalOpen] = useState(false);


    const [paginationInfo, setPaginationInfo] = useState({
        pageNumber: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0
    });

    const fetchMappedUsers = async (pageNumber = 0, pageSize = 10) => {
        try {
            setLoading(true);
            const { mappedUsers, paginationInfo } = await getMappedUsers(pageNumber, pageSize);
            setMappedUsers(mappedUsers);
            setPaginationInfo(paginationInfo);
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMappedUsers();
    }, []);

    const groupedUsers = mappedUsers.reduce((acc, curr) => {
        const userId = curr.user.id;
        if (!acc[userId]) {
            acc[userId] = { user: curr.user, projects: [] };
        }
        if (curr.project) {
            acc[userId].projects.push({
                ...curr.project,
                mappingId: curr.id,
            });
        }

        return acc;
    }, {});


    const groupedItems = Object.values(groupedUsers);

    // Filter search on grouped items
    const filteredItems = groupedItems;


    const openProjectModal = (projects) => {
        setSelectedProjects(projects); // `projects` should now include full mapping info: { id, project, user }
        setProjectModalOpen(true);
    };

    const closeProjectModal = () => {
        setSelectedProjects([]);
        setProjectModalOpen(false);
    };


    const handlePreviousPage = () => {
        if (paginationInfo.pageNumber > 0) {
            fetchMappedUsers(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
        }
    };

    const handleNextPage = () => {
        if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
            fetchMappedUsers(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Project-User Mapping" breadcrumbItem="Mapped Users" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">
                                        <Link to="#" onClick={() => setModalOpen(true)}>
                                            <Button color="primary" className="add-btn float-end" id="create-btn">
                                                <i className="ri-add-line align-bottom me-1"></i> Map User
                                            </Button>
                                        </Link>

                                    </h4>
                                </CardHeader>

                                <CardBody>
                                    <Row className="g-4 mb-3">
                                        <Col className="col-sm">
                                            <div className="d-flex justify-content-sm-end">
                                                <div className="search-box ms-2">
                                                    <input
                                                        type="text"
                                                        className="form-control search"
                                                        placeholder="Search by name, email, project..."
                                                        // value={searchQuery}
                                                        // onChange={handleSearchChange}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {loading ? (
                                        <div>Loading...</div>
                                    ) : error ? (
                                        <div>Error fetching mappings: {error.message}</div>
                                    ) : (
                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table className="table align-middle table-nowrap" id="mappedUsersTable">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Mapped To</th>
                                                        <th>Email</th>
                                                        <th>Mobile</th>
                                                        <th>No. of Projects</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredItems.length > 0 ? (
                                                        filteredItems.map((item, index) => (
                                                            <tr key={item.user.id}>
                                                                <td>{paginationInfo.pageNumber * paginationInfo.pageSize + index + 1}</td>
                                                                <td>{item.user.first_name} {item.user.last_name}</td>
                                                                <td>{item.user.email}</td>
                                                                <td>{item.user.mobile_number}</td>
                                                                <td>
                                                                    <Button
                                                                        color="primary"
                                                                        size="sm"
                                                                        onClick={() => openProjectModal(item.projects)}
                                                                        className="rounded-circle"
                                                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                                                        title="View Projects"
                                                                    >
                                                                        {item.projects.length}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="9" className="text-center">
                                                                No records found.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <Pagination
                                        paginationInfo={paginationInfo}
                                        handleNextPage={handleNextPage}
                                        handlePreviousPage={handlePreviousPage}
                                        onPageChange={(pageNumber, pageSize) => fetchMappedUsers(pageNumber, pageSize)}
                                        onPageSizeChange={(newPageSize) => fetchMappedUsers(0, newPageSize)}
                                    />

                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <MapUserModal
                    isOpen={modalOpen}
                    toggle={() => setModalOpen(false)}
                    onSuccess={fetchMappedUsers}
                />
                <Modal
                    isOpen={projectModalOpen}
                    toggle={closeProjectModal}
                    backdrop="static"
                    keyboard={false}
                    className="modal-dialog modal-lg"
                >
                    <div className="modal-header">
                        <h5 className="modal-title">Mapped Projects</h5>
                        <button type="button" className="btn-close" onClick={closeProjectModal}></button>
                    </div>
                    <div className="modal-body">
                        {selectedProjects.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Project</th>
                                            <th>Council</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProjects.map((project, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{project.name}</td>
                                                <td>{project.council?.name || "—"}</td>
                                                <td>
                                                    <span
                                                        className={`badge text-uppercase ${project.status === "COMPLETED"
                                                                ? "bg-success"
                                                                : project.status === "IN_PROGRESS"
                                                                    ? "bg-warning text-dark"
                                                                    : project.status === "PLANNED"
                                                                        ? "bg-secondary"
                                                                        : project.status === "CANCELED"
                                                                            ? "bg-danger"
                                                                            : project.status === "STOPPED"
                                                                                ? "bg-dark"
                                                                                : "bg-light text-dark"
                                                            }`}
                                                    >
                                                        {project.status.replace(/_/g, " ")}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        title="Unmap project"
                                                        onClick={() =>
                                                            confirmDelete(project.mappingId, () => {
                                                                fetchMappedUsers();
                                                                closeProjectModal(); // or update selectedProjects state manually if needed
                                                            })
                                                        }
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        ) : (
                            <p>No projects mapped.</p>
                        )}
                    </div>
                </Modal>


                <ConfirmDialog />
            </div>
        </React.Fragment>
    );
};

export default MappedUsersListTables;
